import { DomainError } from "@/data/repository"

// Mescla de três versões para quando duas pessoas editam o mesmo registro.
// `base` é a versão que a pessoa abriu, `mine` é o formulário e `theirs` é a versão salva por outra pessoa
// nesse meio-tempo. Campos mudados só de um lado são juntados; mudados dos dois lados viram conflito.

export type MergePath = string[]
export type MergeSide = "mine" | "theirs"
/** Escolha por conflito, indexada por `MergeConflict.id`. */
export type MergeChoices = Record<string, MergeSide>

export interface MergeConflict {
  id: string
  label: string
  /** Valores já formatados para exibição, um por campo do grupo. */
  mine: string[]
  theirs: string[]
}

export interface MergeOptions {
  /** Nome legível do campo; sem nome, o conflito é agrupado no ancestral mais próximo que tenha um. */
  label?: (path: MergePath) => string | undefined
  /**
   * Listas mescladas elemento a elemento, pela chave indicada (ex.: `{ technicalValues: "fieldId" }`).
   * O caminho usa `*` no lugar das chaves dos elementos (ex.: `items.*.technicalValues`).
   * As demais listas são comparadas inteiras.
   */
  keyedArrays?: Record<string, string>
  /** Listas de valores simples tratadas como conjuntos: acréscimos e remoções dos dois lados valem, sem conflito. */
  setArrays?: string[]
  /** Junta campos que mudam juntos (ex.: link, nome e data de um arquivo) num conflito só, com uma escolha. */
  group?: (path: MergePath) => string | undefined
  /** Formata um valor para o diálogo de conflito. */
  format?: (path: MergePath, value: unknown) => string | undefined
}

export interface MergeResult<T> {
  merged: T
  conflicts: MergeConflict[]
}

/** Segmento usado no caminho do conflito de ordem de uma lista mesclada por chave. */
export const ORDER_SEGMENT = "#ordem"
const fallbackLabel = "Outros campos"

type LeafConflict = { path: MergePath; mine: unknown; theirs: unknown }

/** Mescla `mine` e `theirs` a partir de `base`. Com `choices`, os conflitos escolhidos são resolvidos. */
export function threeWayMerge<T>(base: T, mine: T, theirs: T, options: MergeOptions = {}, choices: MergeChoices = {}): MergeResult<T> {
  const leaves: LeafConflict[] = []
  const groupOf = (path: MergePath) => conflictGroup(path, options)
  const pick = (path: MergePath, mineValue: unknown, theirsValue: unknown) => {
    const choice = choices[groupOf(path).id]
    if (choice) return choice === "mine" ? mineValue : theirsValue
    leaves.push({ path, mine: mineValue, theirs: theirsValue })
    return mineValue
  }
  const merged = mergeValue(base, mine, theirs, [], [], options, pick) as T
  return { merged, conflicts: groupConflicts(leaves, options) }
}

type Pick = (path: MergePath, mine: unknown, theirs: unknown) => unknown

// `pattern` acompanha `path`, com `*` no lugar das chaves dos elementos de listas.
function mergeValue(base: unknown, mine: unknown, theirs: unknown, path: MergePath, pattern: MergePath, options: MergeOptions, pick: Pick): unknown {
  // Valores equivalentes ficam com o salvo, para o rascunho rebaseado não parecer alterado.
  if (sameValue(mine, theirs)) return theirs
  if (sameValue(base, mine)) return theirs
  if (sameValue(base, theirs)) return mine
  if (options.setArrays?.includes(pattern.join(".")) && [base ?? [], mine, theirs].every((value) => Array.isArray(value) && value.every((entry) => typeof entry !== "object"))) {
    const [baseSet, mineSet] = [new Set(base as unknown[] ?? []), new Set(mine as unknown[])]
    const added = (mine as unknown[]).filter((entry) => !baseSet.has(entry))
    const removed = new Set([...baseSet].filter((entry) => !mineSet.has(entry)))
    return [...new Set([...(theirs as unknown[]), ...added])].filter((entry) => !removed.has(entry))
  }
  const key = options.keyedArrays?.[pattern.join(".")]
  if (key && isObjectArray(base ?? []) && isObjectArray(mine) && isObjectArray(theirs)) {
    return mergeKeyedArray((base ?? []) as Record<string, unknown>[], mine as Record<string, unknown>[], theirs as Record<string, unknown>[], key, path, pattern, options, pick)
  }
  if (isPlainObject(mine) && isPlainObject(theirs) && (base === undefined || base === null || isPlainObject(base))) {
    const baseObject = (base ?? {}) as Record<string, unknown>
    const result: Record<string, unknown> = {}
    const keys = new Set([...Object.keys(theirs), ...Object.keys(mine), ...Object.keys(baseObject)])
    for (const field of keys) {
      const value = mergeValue(baseObject[field], mine[field], theirs[field], [...path, field], [...pattern, field], options, pick)
      if (value !== undefined) result[field] = value
    }
    return result
  }
  return pick(path, mine, theirs)
}

function mergeKeyedArray(base: Record<string, unknown>[], mine: Record<string, unknown>[], theirs: Record<string, unknown>[], key: string, path: MergePath, pattern: MergePath, options: MergeOptions, pick: Pick): Record<string, unknown>[] {
  // Elementos sem chave (ainda não gravados) contam como acrescentados pelo lado em que aparecem.
  const keyed = (entries: Record<string, unknown>[], side: string) => entries.map((entry, index) => [entry[key] === undefined || entry[key] === null ? `#novo:${side}:${index}` : String(entry[key]), entry] as const)
  const [baseEntries, mineEntries, theirsEntries] = [keyed(base, "base"), keyed(mine, "mine"), keyed(theirs, "theirs")]
  const [baseById, mineById, theirsById] = [new Map(baseEntries), new Map(mineEntries), new Map(theirsEntries)]
  const merged = new Map<string, Record<string, unknown>>()
  for (const id of new Set([...baseById.keys(), ...mineById.keys(), ...theirsById.keys()])) {
    const value = mergeValue(baseById.get(id), mineById.get(id), theirsById.get(id), [...path, id], [...pattern, "*"], options, pick)
    if (value !== undefined) merged.set(id, value as Record<string, unknown>)
  }
  // Ordem: fica a de quem reordenou; se os dois reordenaram de jeitos diferentes, é um conflito.
  const ids = [...merged.keys()]
  const orderOf = (entries: ReturnType<typeof keyed>) => entries.map(([id]) => id).filter((id) => merged.has(id))
  const [baseOrder, mineOrder, theirsOrder] = [orderOf(baseEntries), orderOf(mineEntries), orderOf(theirsEntries)]
  const mineReordered = !sameRelativeOrder(baseOrder, mineOrder)
  const theirsReordered = !sameRelativeOrder(baseOrder, theirsOrder)
  let primary = mineReordered ? mineOrder : theirsOrder
  if (mineReordered && theirsReordered && !sameRelativeOrder(mineOrder, theirsOrder)) {
    primary = pick([...path, ORDER_SEGMENT], mineOrder, theirsOrder) as string[]
  }
  const order = withMissing(primary, [mineOrder, theirsOrder, ids])
  return order.map((id) => merged.get(id)!)
}

/**
 * Acrescenta à ordem principal os ids que só existem nas outras listas: logo depois do vizinho anterior ou, se
 * estavam no fim da lista de origem, no fim.
 */
function withMissing(primary: string[], sources: string[][]) {
  const order = [...primary]
  for (const source of sources) {
    source.forEach((id, index) => {
      if (order.includes(id)) return
      const atEnd = !source.slice(index + 1).some((candidate) => order.includes(candidate))
      const previous = source.slice(0, index).reverse().find((candidate) => order.includes(candidate))
      order.splice(atEnd ? order.length : previous === undefined ? 0 : order.indexOf(previous) + 1, 0, id)
    })
  }
  return order
}

function sameRelativeOrder(left: string[], right: string[]) {
  const common = new Set(left.filter((id) => right.includes(id)))
  const a = left.filter((id) => common.has(id))
  const b = right.filter((id) => common.has(id))
  return a.every((id, index) => id === b[index])
}

function conflictGroup(path: MergePath, options: MergeOptions): { id: string; label: string } {
  const group = options.group?.(path)
  if (group) return { id: group, label: options.label?.(path) ?? fallbackLabel }
  for (let length = path.length; length > 0; length -= 1) {
    const candidate = path.slice(0, length)
    const label = options.label?.(candidate)
    if (label) return { id: candidate.join("."), label }
  }
  return { id: "", label: fallbackLabel }
}

function groupConflicts(leaves: LeafConflict[], options: MergeOptions): MergeConflict[] {
  const groups = new Map<string, MergeConflict>()
  for (const leaf of leaves) {
    const group = conflictGroup(leaf.path, options)
    const entry = groups.get(group.id) ?? { id: group.id, label: group.label, mine: [], theirs: [] }
    entry.mine.push(formatValue(leaf.path, leaf.mine, options))
    entry.theirs.push(formatValue(leaf.path, leaf.theirs, options))
    groups.set(group.id, entry)
  }
  return [...groups.values()]
}

const emptyText = "(vazio)"

export function formatValue(path: MergePath, value: unknown, options: MergeOptions = {}): string {
  const custom = options.format?.(path, value)
  if (custom !== undefined) return custom
  if (isEmpty(value)) return emptyText
  if (typeof value === "boolean") return value ? "Sim" : "Não"
  if (typeof value === "string" || typeof value === "number") return String(value)
  if (Array.isArray(value)) return value.map((entry) => formatValue(path, entry, options)).join(", ")
  if (isPlainObject(value)) {
    const parts = Object.values(value).filter((entry) => !isEmpty(entry) && typeof entry !== "object").map(String)
    return parts.length ? parts.join(" · ") : emptyText
  }
  return String(value)
}

/** Vazio: `undefined`, `null` e texto em branco são equivalentes (formulários trocam um pelo outro). */
function isEmpty(value: unknown) {
  return value === undefined || value === null || (typeof value === "string" && value.trim() === "")
}

export function sameValue(left: unknown, right: unknown): boolean {
  if (isEmpty(left) && isEmpty(right)) return true
  if (isEmpty(left) || isEmpty(right)) return false
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false
    return left.every((entry, index) => sameValue(entry, right[index]))
  }
  if (isPlainObject(left) && isPlainObject(right)) {
    const keys = new Set([...Object.keys(left), ...Object.keys(right)])
    return [...keys].every((key) => sameValue(left[key], right[key]))
  }
  return Object.is(left, right)
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function isObjectArray(value: unknown): value is Record<string, unknown>[] {
  return Array.isArray(value) && value.every(isPlainObject)
}

export function isConcurrencyConflict(cause: unknown) {
  return cause instanceof DomainError && cause.code === "CONCURRENCY_CONFLICT"
}

/** A pessoa fechou o diálogo de conflito: o rascunho fica no formulário e nada é gravado. */
export class MergeCancelledError extends Error {
  constructor() {
    super("Gravação cancelada para revisar as alterações.")
  }
}

export function isMergeCancelled(cause: unknown) {
  return cause instanceof MergeCancelledError
}

export interface SaveWithMergeInput<TInput, TResult> {
  base: TInput
  mine: TInput
  version: number
  save: (input: TInput, version: number) => Promise<TResult>
  /** Versão atual, lida sem passar pelo cache (para não recriar a tela e perder o rascunho). */
  loadLatest: () => Promise<{ input: TInput; version: number }>
  options?: MergeOptions
  /** Abre o diálogo de conflitos; `null` cancela. */
  resolve: (conflicts: MergeConflict[]) => Promise<MergeChoices | null>
  /** Chamado depois de cada mescla com o novo ponto de partida (para retomar após outra confirmação). */
  onRebase?: (state: MergeState<TInput>) => void
  attempts?: number
}

export interface MergeState<TInput> {
  base: TInput
  mine: TInput
  version: number
}

export interface SaveWithMergeResult<TResult> {
  result: TResult
  /** Alterações de outra pessoa foram incorporadas antes de gravar. */
  merged: boolean
}

/** Grava; se outra pessoa salvou antes, mescla com a versão nova e grava de novo. */
export async function saveWithMerge<TInput, TResult>({ base, mine, version, save, loadLatest, options, resolve, onRebase, attempts = 3 }: SaveWithMergeInput<TInput, TResult>): Promise<SaveWithMergeResult<TResult>> {
  let current: MergeState<TInput> = { base, mine, version }
  for (let attempt = 0; ; attempt += 1) {
    try {
      return { result: await save(current.mine, current.version), merged: attempt > 0 }
    } catch (cause) {
      if (!isConcurrencyConflict(cause) || attempt >= attempts) throw cause
    }
    const latest = await loadLatest()
    let result = threeWayMerge(current.base, current.mine, latest.input, options)
    if (result.conflicts.length) {
      const choices = await resolve(result.conflicts)
      if (!choices) throw new MergeCancelledError()
      result = threeWayMerge(current.base, current.mine, latest.input, options, choices)
    }
    current = { base: latest.input, mine: result.merged, version: latest.version }
    onRebase?.(current)
  }
}

/**
 * Para ações que não editam campos (enviar ou apagar um arquivo): se outra pessoa salvou antes, repete a ação
 * sobre a versão atual, sem mesclar nada.
 */
export async function retryWithLatestVersion<TResult>(version: number, run: (version: number) => Promise<TResult>, loadVersion: () => Promise<number>, attempts = 2): Promise<TResult> {
  let current = version
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await run(current)
    } catch (cause) {
      if (!isConcurrencyConflict(cause) || attempt >= attempts) throw cause
    }
    current = await loadVersion()
  }
}

/** Nomes dos campos a partir de um mapa `caminho → nome` (caminho com pontos, como `book.isbn`). */
export function labelsFrom(labels: Record<string, string>): NonNullable<MergeOptions["label"]> {
  return (path) => labels[path.join(".")]
}

export const mergedSaveDescription = "Outra pessoa salvou este registro enquanto você editava. As alterações dela também foram mantidas."
